/**
 * useXoeDictation — browser voice input for the Xoe composer.
 *
 * Extracted verbatim from the old floating window (X16) so that BOTH Xoe surfaces — the floating
 * widget and the full-page workspace at `/xoe` — can offer dictation without either one
 * owning it. It is deliberately a SEPARATE hook from `useXoeChatSession` rather than part
 * of it: nothing here touches the conversation, the turn lifecycle, or the streaming
 * transport, and folding it in would drag `react-speech-recognition` and
 * `NotificationContext` into a hook that otherwise has no notion of either.
 *
 * There is no ordering constraint between this hook and `useXoeChatSession` — the effects
 * below read and write only dictation state — so a caller may declare them in either
 * order. By convention the session hook is declared first.
 *
 * ── What this wraps ──────────────────────────────────────────────────────────
 * The browser's native SpeechRecognition, via `react-speech-recognition` — no backend
 * involved. In Chromium browsers that means the audio is sent to Google's cloud speech
 * service; some privacy-focused browsers (notably Brave, via its Shields feature) block
 * that network call by default, which surfaces as a 'network' error — there is no
 * code-side fix for that, it's a per-browser setting.
 *
 * ⚠️ Because this is the browser's own service and not ours, dictated audio is NOT
 * covered by `https://wiki.mygene.com.au/Information-Technology/Xoe/compliance/ai-data-egress-position`'s register of AI call sites.
 * Nothing here posts to Anthropic or OpenAI; the transcript reaches our servers only if
 * the practitioner then sends the composed message as a turn.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

import { useNotifications } from '../../context/NotificationContext';
import type { XoeDictation } from '../../types/xoe/XoeChatTypes';

/** Human-readable explanation for the Web Speech API's error codes (there is no
 *  built-in message — just a short code). */
function describeSpeechRecognitionError(code: string): string {
	switch (code) {
		case 'not-allowed':
			return 'Microphone access was blocked. Check the browser\'s site permissions, and note voice input requires HTTPS or localhost.';
		case 'service-not-allowed':
			return 'Voice recognition isn\'t available on this connection — it requires HTTPS or localhost.';
		case 'audio-capture':
			return 'No microphone was found.';
		case 'no-speech':
			return 'No speech was detected.';
		case 'network':
			return "Couldn't reach the voice recognition service. Check your internet connection, VPN/firewall restrictions, or (in Brave) disable Shields for this site — Brave blocks this by default.";
		case 'aborted':
			return 'Voice input was cancelled.';
		default:
			return `Voice input error: ${code}`;
	}
}

export function useXoeDictation(): XoeDictation {
	const [dictatedText, setDictatedText] = useState('');
	const [dictationToken, setDictationToken] = useState(0);
	const {
		finalTranscript,
		resetTranscript,
		listening: micActive,
		browserSupportsSpeechRecognition: micSupported,
	} = useSpeechRecognition();
	const { showError } = useNotifications();

	// True once this listening session has already retried a 'network' error once —
	// reset per toggle-on so every fresh press of the mic gets its own single retry.
	const networkRetriedRef = useRef(false);

	const beginListening = useCallback(() => {
		void SpeechRecognition.startListening({ continuous: true, language: 'en-AU' }).then(() => {
			// The library manages its own onresult/onend on the native recognition object
			// for its listening-state/restart logic — wrap (never replace) onerror so we
			// only add diagnostics without breaking that. Guarded so this only wraps once,
			// since getRecognition() returns the same long-lived native instance every call.
			const recognition = SpeechRecognition.getRecognition();
			if (!recognition || (recognition as any).__mgVoiceErrorHooked) return;

			const originalOnError = recognition.onerror;
			recognition.onerror = (event) => {
				originalOnError?.call(recognition, event);

				// eslint-disable-next-line no-console
				console.error('[useXoeDictation] voice input error', event.error);

				// 'network' can be a transient hiccup reaching the browser's speech
				// service — retry once (silently) before bothering the practitioner with it.
				if (event.error === 'network' && !networkRetriedRef.current) {
					networkRetriedRef.current = true;
					beginListening();
					return;
				}

				if (event.error !== 'aborted') {
					showError('Voice input stopped', describeSpeechRecognitionError(event.error));
				}
			};
			(recognition as any).__mgVoiceErrorHooked = true;
		});
	}, [showError]);

	const toggleMic = useCallback(() => {
		if (micActive) {
			void SpeechRecognition.stopListening();
			return;
		}
		if (!micSupported) return;
		resetTranscript();
		networkRetriedRef.current = false;
		beginListening();
	}, [micActive, micSupported, resetTranscript, beginListening]);

	// Feed each finished utterance into the composer, then reset so the next one
	// arrives as its own discrete chunk (matches the old per-utterance behaviour).
	useEffect(() => {
		if (finalTranscript) {
			setDictatedText(finalTranscript);
			setDictationToken((t) => t + 1);
			resetTranscript();
		}
	}, [finalTranscript, resetTranscript]);

	// Stop any in-progress recognition when the hosting surface unmounts.
	useEffect(() => () => { void SpeechRecognition.abortListening(); }, []);

	return { dictatedText, dictationToken, micActive, micSupported, toggleMic };
}

export default useXoeDictation;
